<script setup>
import { ref, onMounted } from 'vue'
import { axios } from '@/utils/axios'
import { Heading } from '@/components/ui/typography'

// Organization count
const totalOrganizationsCount = ref('--')

const fetchOrganizationCount = async () => {
    try {
        const response = await axios.get('/organizations/all', {
            params: {
                page: 1,
                limit: 1
            }
        })

        if (response.data.payload?.meta?.organizations_count !== undefined) {
            totalOrganizationsCount.value = response.data.payload.meta.organizations_count
        }
    } catch (error) {
        console.error('Failed to fetch organization count:', error)
    }
}

onMounted(() => {
    fetchOrganizationCount()
})

// Navigation items for admin area
const navigationItems = [
    {
        name: 'Organizations',
        routeName: 'admin-organizations',
        icon: 'building',
        paths: ['text-slate-400', 'text-slate-600'],
        description: 'Manage customer organizations and subscriptions'
    },
    {
        name: 'System Status',
        routeName: 'admin-system-status',
        icon: 'heartbeat',
        paths: ['text-slate-400', 'text-slate-600'],
        description: 'Monitor ETL pipeline health and performance'
    }
]
</script>

<template>
    <div>
        <!-- Header Section -->
        <div class="mb-8">
            <Heading title="Admin Dashboard" subtitle="System Administration" :level="2" />
            <div class="mt-2 inline-flex rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-800">
                Staff Only
            </div>
        </div>

        <!-- Navigation Menu -->
        <div class="mb-6">
            <div class="relative mb-2 flex h-8 justify-between border-b border-slate-200 pb-1.5">
                <h4 class="my-auto text-sm font-semibold leading-none text-slate-700">Menu</h4>
            </div>

            <ul class="flex flex-col flex-nowrap">
                <router-link
                    v-for="item in navigationItems"
                    :key="item.routeName"
                    v-slot="{ href, navigate, isExactActive }"
                    :to="{ name: item.routeName }"
                    custom
                >
                    <li class="mr-0.5 md:mb-0.5 md:mr-0">
                        <a
                            class="flex items-center whitespace-nowrap rounded px-2.5 py-2 transition-colors duration-150"
                            :class="isExactActive ? 'bg-white shadow-sm' : 'hover:bg-slate-50'"
                            :href="href"
                            @click="navigate"
                        >
                            <AppIcon
                                :name="item.icon"
                                class="mr-2 h-4 w-4 shrink-0"
                            />
                            <span
                                class="text-sm font-medium"
                                :class="isExactActive ? 'text-indigo-500' : 'text-slate-600'"
                            >
                                {{ item.name }}
                            </span>
                        </a>
                    </li>
                </router-link>
            </ul>
        </div>

        <!-- Quick Actions Section -->
        <div class="mb-6">
            <div class="relative mb-2 flex h-8 justify-between border-b border-slate-200 pb-1.5">
                <h4 class="my-auto text-sm font-semibold leading-none text-slate-700">Quick Actions</h4>
            </div>

            <div class="space-y-2">
                <div class="rounded-lg bg-slate-50 p-3">
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium text-slate-700">System Health</span>
                        <div class="flex h-2 w-2 rounded-full bg-green-400"></div>
                    </div>
                    <p class="mt-1 text-xs text-slate-500">All systems operational</p>
                </div>

                <div class="rounded-lg bg-slate-50 p-3">
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium text-slate-700">Total Organizations</span>
                        <span class="text-sm font-semibold text-slate-800">{{ totalOrganizationsCount }}</span>
                    </div>
                    <p class="mt-1 text-xs text-slate-500">Active customer accounts</p>
                </div>
            </div>
        </div>

        <!-- Admin Tools Section -->
        <!-- <div class="mb-6">
            <div class="relative mb-2 flex h-8 justify-between border-b border-slate-200 pb-1.5">
                <h4 class="my-auto text-sm font-semibold leading-none text-slate-700">Admin Tools</h4>
            </div>

            <ul class="flex flex-col flex-nowrap">
                <li class="mr-0.5 md:mb-0.5 md:mr-0">
                    <a
                        class="flex items-center whitespace-nowrap rounded px-2.5 py-2 transition-colors duration-150 hover:bg-slate-50"
                        href="#"
                        @click.prevent
                    >
                        <AppIcon
                            name="download"
                            class="mr-2 h-4 w-4 shrink-0 text-slate-400"
                        />
                        <span class="text-sm font-medium text-slate-600">Export Data</span>
                    </a>
                </li>

                <li class="mr-0.5 md:mb-0.5 md:mr-0">
                    <a
                        class="flex items-center whitespace-nowrap rounded px-2.5 py-2 transition-colors duration-150 hover:bg-slate-50"
                        href="#"
                        @click.prevent
                    >
                        <AppIcon
                            name="cog"
                            class="mr-2 h-4 w-4 shrink-0 text-slate-400"
                        />
                        <span class="text-sm font-medium text-slate-600">System Settings</span>
                    </a>
                </li>
            </ul>
        </div> -->

        <!-- Footer Info -->
        <!-- <div class="mt-auto pt-6">
            <div class="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <div class="flex items-start">
                    <AppIcon
                        name="exclamation-triangle"
                        class="mr-2 mt-0.5 h-4 w-4 shrink-0 text-amber-500"
                    />
                    <div>
                        <p class="text-xs font-medium text-amber-800">Admin Access</p>
                        <p class="mt-1 text-xs text-amber-700">
                            You have administrative privileges. Use responsibly.
                        </p>
                    </div>
                </div>
            </div>
        </div> -->
    </div>
</template>

<style scoped>
/* Custom scrollbar styling to match campaigns page */
.scrollbar::-webkit-scrollbar {
    width: 8px;
}

.scrollbar::-webkit-scrollbar-track {
    @apply bg-slate-100;
}

.scrollbar::-webkit-scrollbar-thumb {
    @apply bg-slate-200 rounded-full;
}

.scrollbar::-webkit-scrollbar-thumb:hover {
    @apply bg-slate-300;
}
</style>